import { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { useLocation } from 'libs/router'

import { PageHeaderContext } from 'store'
import { SearchInput } from 'shared/components'
import { IconButton } from 'shared/atoms'
import { useDevice } from 'shared/hooks'
import { cn } from 'utils/style'

type HeaderProps = {
	title: string
	eyebrow?: string
	actions?: ReactNode
	tabs?: ReactNode
	handleSearch?: (event: ChangeEvent<HTMLInputElement>) => void
}

const SETTINGS_SEGMENTS = [
	'store',
	'categories',
	'fabrics',
	'conditions',
	'genders',
	'types',
	'tags',
	'coupons',
	'area-types',
	'areas',
	'floor-map',
	'rentals',
	'opening-hours'
]

const useSectionLabel = () => {
	const { t } = useTranslation()
	const { pathname } = useLocation()
	const segment = pathname.split('/')[1] ?? ''

	if (SETTINGS_SEGMENTS.includes(segment)) return t('Settings')

	const labels: Record<string, string> = {
		'': t('Overview'),
		sales: t('Revenue'),
		products: t('Catalog'),
		bookings: t('Rental'),
		payouts: t('Payments'),
		users: t('Users'),
		profile: t('Account')
	}

	return labels[segment]
}

export const Header = ({ title, eyebrow, actions, tabs, handleSearch }: HeaderProps) => {
	const { t } = useTranslation()
	const containers = useContext(PageHeaderContext)
	const [searchOpened, setSearchOpened] = useState(false)
	const { isMobileOrTabletHorizontal } = useDevice()
	const sectionLabel = useSectionLabel()
	const label = eyebrow ?? sectionLabel
	const hasTabs = !!tabs
	const setHasTabs = containers?.setHasTabs

	useEffect(() => {
		if (!setHasTabs || !hasTabs) return

		setHasTabs(true)

		return () => setHasTabs(false)
	}, [setHasTabs, hasTabs])

	if (!containers?.main) return null

	return (
		<>
			{createPortal(
				isMobileOrTabletHorizontal && searchOpened && handleSearch ? (
					<div className="flex w-full items-center gap-3 [&>div]:w-full">
						<SearchInput
							className="border-none [&_input:focus]:shadow-none [&_input]:border-none [&_input]:py-0 [&_input]:text-lg [&_input]:shadow-none"
							placeholder={t('Search')}
							label=""
							onChange={handleSearch}
						/>
						<IconButton aria-label={t('Close search')} onClick={() => setSearchOpened(false)}>
							<XMarkIcon />
						</IconButton>
					</div>
				) : (
					<>
						<div className="min-w-0">
							{label && (
								<p className="truncate text-[10px] font-semibold uppercase leading-normal tracking-widest text-muted-foreground">
									{label}
								</p>
							)}
							<h1
								className={cn(
									'truncate font-display text-2xl font-bold text-foreground',
									hasTabs ? 'leading-tight' : 'leading-none'
								)}>
								{title}
							</h1>
						</div>
						{(actions || (isMobileOrTabletHorizontal && handleSearch)) && (
							<div className="flex shrink-0 items-center gap-2">
								{isMobileOrTabletHorizontal && handleSearch && (
									<IconButton aria-label={t('Search')} onClick={() => setSearchOpened(true)}>
										<MagnifyingGlassIcon />
									</IconButton>
								)}
								{actions}
							</div>
						)}
					</>
				),
				containers.main
			)}
			{tabs && containers.tabs && createPortal(tabs, containers.tabs)}
		</>
	)
}
